import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { TooltipContentProps } from 'recharts/types/component/Tooltip'
import { TooltipPayloadEntry } from 'recharts/types/state/tooltipSlice'

import { useRegion } from 'components/app-shell/context/RegionContext'
import { tx } from 'lib/i18n'

export interface SiteStatusCounts {
  site: string
  approved: number
  submitted: number
  draft: number
  overdue: number
}

const SERIES: { key: keyof Omit<SiteStatusCounts, 'site'>; color: string; ar: string; en: string }[] = [
  { ar: 'معتمد', color: 'var(--chart-approved)', en: 'Approved', key: 'approved' },
  { ar: 'بانتظار الاعتماد', color: 'var(--chart-submitted)', en: 'Waiting for approval', key: 'submitted' },
  { ar: 'مسودة', color: 'var(--chart-draft)', en: 'Draft', key: 'draft' },
  { ar: 'متأخر', color: 'var(--chart-overdue)', en: 'Overdue', key: 'overdue' },
]

const ChartTooltip = ({ active, payload, label }: TooltipContentProps) => {
  const { locale } = useRegion()
  if (!active || !payload?.length) return null
  const total = payload.reduce((sum: number, entry: TooltipPayloadEntry) => sum + (Number(entry.value) || 0), 0)
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip__title">{label}</p>
      {SERIES.map((series) => (
        <div className="chart-tooltip__row" key={series.key}>
          <span className="chart-tooltip__dot" style={{ background: series.color }} />
          <span className="chart-tooltip__label">{tx(locale, series.ar, series.en)}</span>
          <strong>{payload.find((entry: TooltipPayloadEntry) => entry.dataKey === series.key)?.value ?? 0}</strong>
        </div>
      ))}
      <div className="chart-tooltip__row chart-tooltip__row--total">
        <span className="chart-tooltip__label">{tx(locale, 'الإجمالي', 'Total')}</span>
        <strong>{total}</strong>
      </div>
    </div>
  )
}

/** "Report status by site", last 30 days (Figma Chart/StackedBar). */
export const ReportStatusChart = ({ data }: { data: SiteStatusCounts[] }) => {
  const { locale } = useRegion()
  return (
  <div className="status-chart">
    <div className="status-chart__plot">
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={data} margin={{ bottom: 0, left: 0, right: 0, top: 8 }}>
          <CartesianGrid stroke="var(--chart-grid, #e5e7eb)" vertical={false} />
          <XAxis dataKey="site" tick={{ fill: 'var(--ink-2)', fontSize: 12 }} tickLine={false} axisLine={{ stroke: 'var(--line)' }} />
          <YAxis
            allowDecimals={false}
            tick={{ fill: 'var(--ink-2)', fontSize: 12 }}
            tickLine={false}
            axisLine={false}
            label={{ angle: -90, fill: 'var(--ink-2)', fontSize: 12, position: 'insideLeft', value: tx(locale, 'تقارير', 'Reports') }}
          />
          <Tooltip content={ChartTooltip} cursor={{ fill: 'var(--secondary)' }} />
          {SERIES.map((series, index) => (
            <Bar
              key={series.key}
              dataKey={series.key}
              name={tx(locale, series.ar, series.en)}
              stackId="status"
              fill={series.color}
              barSize={48}
              radius={index === SERIES.length - 1 ? [4, 4, 0, 0] : undefined}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
    <div className="status-chart__legend">
      {SERIES.map((series) => (
        <span key={series.key}>
          <span className="status-chart__swatch" style={{ background: series.color }} />
          {tx(locale, series.ar, series.en)}
        </span>
      ))}
    </div>
  </div>
  )
}
